import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, ArrowUpRight, CheckCircle2, Instagram, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';

const BASE = process.env.REACT_APP_BACKEND_URL;
const STORAGE = 'advecti-instagram-invite';
const messages = {
  success: ['Instagram connected', 'Your account is connected to the campaign. You can close this page.'],
  cancelled: ['Connection cancelled', 'No new connection was completed. You can try again with the original link.'],
  'wrong-account': ['Different account selected', 'Please reopen your link and sign into the Instagram account named in the invitation.'],
  failed: ['Connection not completed', 'Reopen your invitation to try again. If it is no longer available, request a new link from Advecti.'],
};

export default function InstagramConnect() {
  const token = useRef('');
  const entry = useRef({
    fresh: new URLSearchParams(window.location.hash.slice(1)).get('invite'),
    result: new URLSearchParams(window.location.search).get('result'),
  });
  const [info, setInfo] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = 'Connect Instagram | Advecti';
    const { fresh, result } = entry.current;
    // A generic result is safe to retain so refresh preserves the confirmation.
    window.history.replaceState(null, '', window.location.pathname + (messages[result] ? `?result=${result}` : ''));
    try {
      if (messages[result]) {
        sessionStorage.removeItem(STORAGE); setStatus(result); return;
      }
      if (fresh) sessionStorage.setItem(STORAGE, fresh);
      token.current = fresh || sessionStorage.getItem(STORAGE) || '';
    } catch { setStatus('unavailable'); return; }
    if (!/^[A-Za-z0-9_-]{43}$/.test(token.current)) { setStatus('unavailable'); return; }
    let cancelled = false;
    fetch(`${BASE}/api/bloom/connect/inspect`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: token.current }), cache: 'no-store',
    }).then(async (response) => {
      if (!response.ok) throw new Error('Unavailable');
      const data = await response.json();
      if (!cancelled) { setInfo(data); setStatus('ready'); }
    }).catch(() => { if (!cancelled) setStatus('unavailable'); });
    return () => { cancelled = true; };
  }, []);

  const connect = async () => {
    setBusy(true); setError('');
    try {
      const response = await fetch(`${BASE}/api/bloom/connect/start`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin', body: JSON.stringify({ token: token.current }), cache: 'no-store',
      });
      if (!response.ok) throw new Error('This link is unavailable or Instagram cannot be reached. Please try again or request a new link.');
      const data = await response.json();
      window.location.assign(data.url);
    } catch (e) { setError(e.message); setBusy(false); }
  };
  const result = messages[status] || ['Connection link unavailable', 'This link may have expired, been used, or been revoked. Please request a new link from Advecti.'];

  return <div className="min-h-screen bg-cream-light text-navy px-6 py-8 sm:py-12" data-testid="instagram-connect-page">
    <header className="max-w-5xl mx-auto border-b border-navy/10 pb-6">
      <img src="/advecti-logo.png" alt="Advecti" className="w-32 h-auto" data-testid="instagram-connect-logo" />
    </header>
    <div className="max-w-lg mx-auto py-12 sm:py-20">
      {status === 'loading' ? <div role="status" className="flex items-center gap-3" data-testid="instagram-connect-loading"><Loader2 className="animate-spin" /> Checking invitation…</div> : status === 'ready' ? <>
        <Instagram size={36} className="text-pink-600 mb-6" />
        <p className="text-sm text-navy/60 font-semibold mb-3 break-words" data-testid="instagram-connect-brand">{info.brand_label}</p>
        <h1 className="text-4xl sm:text-5xl font-bold mb-5" data-testid="instagram-connect-title">Connect Instagram</h1>
        <p className="text-lg font-semibold break-all mb-6" data-testid="instagram-connect-handle">@{info.ig_username}</p>
        <div className="border-y border-navy/10 py-5 space-y-3 text-sm text-navy/75" data-testid="instagram-connect-permissions">
          <p>Authorize access to profile details, follower counts, posts, and giveaway comments.</p>
          <p>You approve access on Instagram. Your password is never shared with Advecti.</p>
        </div>
        <Button onClick={connect} disabled={busy} className="w-full h-auto min-h-12 whitespace-normal bg-pink-600 hover:bg-pink-700 text-white mt-7" data-testid="instagram-connect-submit">{busy ? <Loader2 className="animate-spin" /> : <Instagram />}{busy ? 'Opening Instagram…' : 'Continue with Instagram'}<ArrowUpRight /></Button>
        {error && <p role="alert" className="text-sm text-red-700 mt-4" data-testid="instagram-connect-error">{error}</p>}
        <p className="text-xs text-navy/60 mt-5 flex gap-2 items-start" data-testid="instagram-connect-expiry"><ShieldCheck size={16} className="shrink-0" /> Single-use invitation · Expires {new Date(info.expires_at).toLocaleString()}</p>
      </> : <div role="status" data-testid={`instagram-connect-result-${status}`}>
        {status === 'success' ? <CheckCircle2 size={44} className="text-green-700 mb-6" /> : <AlertCircle size={44} className="text-pink-600 mb-6" />}
        <h1 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="instagram-connect-result-title">{result[0]}</h1>
        <p className="text-base text-navy/70" data-testid="instagram-connect-result-message">{result[1]}</p>
      </div>}
      <footer className="mt-12 text-xs text-navy/60"><a href="/privacy" className="underline hover:text-pink-600" data-testid="instagram-connect-privacy">Privacy policy</a></footer>
    </div>
  </div>;
}